import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { HelpCircle, CheckCircle2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { t, lang } = useLanguage();
  const { items, total, clearCart } = useCart();
  const signatureAllowedForCart = items.some((it) => it.signature_enabled !== false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    customer_address: "", customer_city: "", customer_zip: "",
    payment_method: "qr", note: "",
  });
  const [consent, setConsent] = useState(false);
  const [shippingMethod, setShippingMethod] = useState("packeta"); // pickup | packeta
  const [pickupLocation, setPickupLocation] = useState("");
  const [packetaBranch, setPacketaBranch] = useState("");
  const [packetaPoint, setPacketaPoint] = useState(null); // {id, name, formatedValue, street, city, zip, country}
  const [signatureOn, setSignatureOn] = useState(false);
  const [signatureTo, setSignatureTo] = useState("");
  const [phoneCountry, setPhoneCountry] = useState("+420"); // CZ default, SK = +421
  const [paymentMethod, setPaymentMethod] = useState("qr"); // qr | cash | cod | card_on_pickup | card_online
  const [emailCheck, setEmailCheck] = useState({ status: "idle", reason: "", suggestion: "" }); // idle|checking|ok|bad
  const emailTimer = useRef(null);
  const emailReqId = useRef(0);
  const [shipConfig, setShipConfig] = useState({
    pickup_enabled: true,
    pickup_locations: [],
    packeta_enabled: true,
    packeta_price: 99,
    packeta_api_key: "",
    signature_enabled: true,
    signature_price: 100,
    signature_label: "Speciální požadavek: Podpis Rebelek věnovaný",
    cod_enabled: true,
    cod_fee: 30,
    card_on_pickup_enabled: true,
    card_on_pickup_fee: 0,
    stripe_enabled: true,
    stripe_fee: 0,
    // QR defaults: OFF for pickup, ON for packeta
    qr_enabled_for_pickup: false,
    qr_enabled_for_packeta: true,
  });

  // Load Packeta widget script once
  useEffect(() => {
    if (window.Packeta) return;
    const s = document.createElement("script");
    s.src = "https://widget.packeta.com/v6/www/js/library.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      const s = res.data || {};
      setShipConfig({
        pickup_enabled: s.shipping_pickup_enabled !== false,
        pickup_locations: s.shipping_pickup_locations || [],
        packeta_enabled: s.shipping_packeta_enabled !== false,
        packeta_price: Number(s.shipping_packeta_price) || 99,
        packeta_api_key: s.shipping_packeta_api_key || "",
        ppl_enabled: !!s.shipping_ppl_enabled,
        ppl_price: Number(s.shipping_ppl_price) || 149,
        signature_enabled: s.signature_enabled !== false,
        signature_price: Number(s.signature_price) || 100,
        signature_label: s.signature_label || "Speciální požadavek: Podpis Rebelek věnovaný",
        cod_enabled: s.cod_enabled !== false,
        cod_fee: Number(s.cod_fee) || 30,
        card_on_pickup_enabled: s.card_on_pickup_enabled !== false,
        card_on_pickup_fee: Number(s.card_on_pickup_fee) || 0,
        cash_on_pickup_enabled: !!s.cash_on_pickup_enabled,
        stripe_enabled: s.stripe_enabled !== false,
        stripe_fee: Number(s.stripe_fee) || 0,
        order_info_note: s.order_info_note || "",
        // Explicit false for pickup; packeta defaults to on when unset
        qr_enabled_for_pickup: !!s.qr_enabled_for_pickup,
        qr_enabled_for_packeta: s.qr_enabled_for_packeta !== false,
      });
      if (s.shipping_pickup_locations?.length && !pickupLocation) {
        setPickupLocation(s.shipping_pickup_locations[0].name);
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPacketaWidget = () => {
    if (!window.Packeta || !shipConfig.packeta_api_key) {
      // fallback: focus manual input
      return;
    }
    window.Packeta.Widget.pick(
      shipConfig.packeta_api_key,
      (point) => {
        if (!point) return;
        setPacketaPoint(point);
        setPacketaBranch(point.formatedValue || point.name || "");
      },
      { language: lang === "en" ? "en" : "cs", view: "modal", vendors: [{ country: "cz" }, { country: "sk" }] },
    );
  };

  // Shipping options currently offered (admin toggles)
  const availableShipping = [
    shipConfig.pickup_enabled && "pickup",
    shipConfig.packeta_enabled && "packeta",
    shipConfig.ppl_enabled && "ppl",
  ].filter(Boolean);

  const shippingPrice = shippingMethod === "pickup" ? 0 : (shippingMethod === "ppl" ? shipConfig.ppl_price : shipConfig.packeta_price);
  const signaturePrice = signatureOn && signatureTo.trim() ? shipConfig.signature_price : 0;

  const qrAllowedForShipping =
    (shippingMethod === "pickup" && !!shipConfig.qr_enabled_for_pickup)
    || (shippingMethod === "packeta" && shipConfig.qr_enabled_for_packeta !== false);

  // Allowed payment methods for current shipping (respects QR availability + admin toggles)
  const allowedPayments = (() => {
    if (shippingMethod === "ppl") return shipConfig.stripe_enabled ? ["card_online"] : [];
    const list = [];
    if (shippingMethod === "pickup") {
      if (qrAllowedForShipping) list.push("qr");
      if (shipConfig.cash_on_pickup_enabled) list.push("cash");
      if (shipConfig.stripe_enabled) list.push("card_online");
    } else if (shippingMethod === "packeta") {
      if (qrAllowedForShipping) list.push("qr");
      if (shipConfig.cod_enabled) list.push("cod");
      if (shipConfig.card_on_pickup_enabled) list.push("card_on_pickup");
      if (shipConfig.stripe_enabled) list.push("card_online");
    }
    return list;
  })();

  // COD / card-on-pickup / online card poplatek
  let paymentFee = 0;
  if (paymentMethod === "card_online") {
    paymentFee = shipConfig.stripe_fee;
  } else if (shippingMethod === "packeta") {
    if (paymentMethod === "cod") paymentFee = shipConfig.cod_fee;
    else if (paymentMethod === "card_on_pickup") paymentFee = shipConfig.card_on_pickup_fee;
  }

  let effectivePayment = paymentMethod;
  if (shippingMethod === "ppl") {
    effectivePayment = "card_online";
  } else if (!allowedPayments.includes(paymentMethod)) {
    effectivePayment = allowedPayments[0] || (shipConfig.stripe_enabled ? "card_online" : "qr");
  }
  const grandTotal = total + shippingPrice + signaturePrice + paymentFee;

  // Auto-select sole shipping method (or fix if current option was disabled)
  useEffect(() => {
    if (!availableShipping.length) return;
    if (availableShipping.length === 1) {
      if (shippingMethod !== availableShipping[0]) {
        setShippingMethod(availableShipping[0]);
      }
      return;
    }
    if (!availableShipping.includes(shippingMethod)) {
      setShippingMethod(availableShipping[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shipConfig.pickup_enabled, shipConfig.packeta_enabled, shipConfig.ppl_enabled]);

  // Auto-select sole payment method (or fix when shipping/settings filter options)
  useEffect(() => {
    if (!allowedPayments.length) return;
    if (allowedPayments.length === 1) {
      if (paymentMethod !== allowedPayments[0]) {
        setPaymentMethod(allowedPayments[0]);
      }
      return;
    }
    if (!allowedPayments.includes(paymentMethod)) {
      setPaymentMethod(allowedPayments[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingMethod, shipConfig.qr_enabled_for_pickup, shipConfig.qr_enabled_for_packeta,
      shipConfig.cash_on_pickup_enabled, shipConfig.cod_enabled, shipConfig.card_on_pickup_enabled,
      shipConfig.stripe_enabled]);

  const formatZip = (raw) => {
    const digits = String(raw || "").replace(/\D+/g, "").slice(0, 5);
    if (digits.length <= 3) return digits;
    return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  };

  const zipOk = /^\d{3}\s?\d{2}$/.test((form.customer_zip || "").trim())
    && (form.customer_zip || "").replace(/\D+/g, "").length === 5;
  const addressOk = (form.customer_address || "").trim().length >= 5
    && /\d/.test(form.customer_address || "");
  const nameOk = (form.customer_name || "").trim().split(/\s+/).filter(Boolean).length >= 2;
  const cityOk = (form.customer_city || "").trim().length >= 2;

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "customer_zip") {
      setForm((f) => ({ ...f, customer_zip: formatZip(value) }));
      return;
    }
    if (name === "customer_email") {
      setForm((f) => ({ ...f, customer_email: value.trimStart() }));
      setEmailCheck({ status: "idle", reason: "", suggestion: "" });
      if (emailTimer.current) clearTimeout(emailTimer.current);
      const email = value.trim();
      const reqId = ++emailReqId.current;
      if (!email || !email.includes("@") || email.length < 5) return;
      emailTimer.current = setTimeout(async () => {
        if (reqId !== emailReqId.current) return;
        setEmailCheck({ status: "checking", reason: "", suggestion: "" });
        try {
          const r = await axios.get(`${API}/validate/email`, { params: { email } });
          // Ignore stale responses from a previous address
          if (reqId !== emailReqId.current) return;
          setEmailCheck({
            status: r.data?.ok ? "ok" : "bad",
            reason: r.data?.reason || "",
            suggestion: r.data?.suggestion || "",
          });
        } catch {
          if (reqId !== emailReqId.current) return;
          setEmailCheck({ status: "idle", reason: "", suggestion: "" });
        }
      }, 450);
      return;
    }
    setForm((f) => ({ ...f, [name]: value }));
  };

  const applyEmailSuggestion = () => {
    if (!emailCheck.suggestion) return;
    setForm((f) => ({ ...f, customer_email: emailCheck.suggestion }));
    setEmailCheck({ status: "ok", reason: "", suggestion: "" });
  };

  const FieldLabel = ({ children, hint, htmlFor }) => (
    <label htmlFor={htmlFor} style={{ ...labelStyle, display: "flex", alignItems: "center", gap: "0.35rem" }}>
      <span>{children}</span>
      {hint && (
        <span
          title={hint}
          aria-label={t.checkout.fieldHint}
          style={{ color: "#52525B", cursor: "help", display: "inline-flex", lineHeight: 0 }}
        >
          <HelpCircle size={11} />
        </span>
      )}
    </label>
  );

  const FieldOk = ({ show, text }) => (show ? (
    <p data-testid="field-ok" style={{ color: "#00FF64", fontSize: "0.68rem", marginTop: "0.25rem", fontFamily: "Manrope, sans-serif", display: "flex", alignItems: "center", gap: "0.3rem" }}>
      <CheckCircle2 size={11} /> {text || t.checkout.fieldOk}
    </p>
  ) : null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return;
    if (!consent) {
      setError("Pro odeslání objednávky je nutné odsouhlasit obchodní podmínky a zpracování osobních údajů.");
      return;
    }
    if (!nameOk) {
      setError(lang === "cz" ? "Vyplň jméno i příjmení." : "Please enter first and last name.");
      return;
    }
    if (emailCheck.status === "bad") {
      setError(emailCheck.reason || (lang === "cz" ? "E-mail neprošel kontrolou." : "Email failed validation."));
      return;
    }
    if (!zipOk) {
      setError(lang === "cz" ? "PSČ musí mít formát 123 45." : "ZIP must look like 123 45.");
      return;
    }
    if (!addressOk) {
      setError(lang === "cz" ? "Adresa musí obsahovat ulici a číslo popisné." : "Address must include street and house number.");
      return;
    }
    if (shippingMethod === "pickup" && !pickupLocation) {
      setError("Vyber prosím místo osobního odběru.");
      return;
    }
    if (shippingMethod === "packeta" && !packetaPoint && !packetaBranch) {
      setError("Vyber prosím pobočku Zásilkovny.");
      return;
    }
    // Phone validation — Packeta vyžaduje 9 cifer + správnou předvolbu
    const phoneDigits = (form.customer_phone || "").replace(/\D+/g, "");
    if (phoneDigits.length !== 9) {
      setError(`Telefon musí mít přesně 9 cifer (zadáno ${phoneDigits.length}). Předvolba ${phoneCountry} se přidá automaticky.`);
      return;
    }
    if (phoneCountry === "+420" && !"67".includes(phoneDigits[0])) {
      setError("CZ mobilní telefon musí začínat 6 nebo 7. Pokud máš slovenské číslo, přepni předvolbu na +421.");
      return;
    }
    if (phoneCountry === "+421" && phoneDigits[0] !== "9") {
      setError("SK mobilní telefon musí začínat 9. Pokud máš české číslo, přepni předvolbu na +420.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const orderItems = items.map(i => ({
        product_id: i._id,
        product_name: i.name,
        price: i.price,
        quantity: i.quantity,
        size: i.size || null,
        color: i.color || null,
        custom_options: i.custom_options || null,
      }));
      const fullPhone = phoneCountry + phoneDigits;
      const res = await axios.post(`${API}/orders`, {
        ...form,
        customer_phone: fullPhone,
        payment_method: effectivePayment,
        items: orderItems,
        total: grandTotal,
        gdpr_consent: true,
        shipping_method: shippingMethod,
        shipping_pickup_location: shippingMethod === "pickup" ? pickupLocation : null,
        shipping_packeta_branch: shippingMethod === "packeta" ? (packetaBranch || null) : null,
        shipping_packeta_point_id: shippingMethod === "packeta" ? (packetaPoint?.id || null) : null,
        shipping_packeta_point: shippingMethod === "packeta" ? (packetaPoint || null) : null,
        shipping_price: shippingPrice,
        signature_request: signatureOn && signatureTo.trim() !== "",
        signature_to: signatureOn ? signatureTo.trim() : null,
        signature_price: signaturePrice,
        payment_fee: paymentFee,
      });
      // Online karta → Stripe Checkout session + QR na success stránce
      // Nikdy nepřepínat tiše na bankovní QR při chybě Stripe.
      if (effectivePayment === "card_online" && res.data?._id) {
        try {
          const stripeRes = await axios.post(`${API}/orders/${res.data._id}/stripe-session`, {
            origin_url: window.location.origin,
          });
          if (!stripeRes.data?.url) {
            throw new Error("Stripe nevrátil platební URL");
          }
          const orderPayload = {
            ...res.data,
            payment_method: "card_online",
            stripe_url: stripeRes.data.pay_url || stripeRes.data.url,
            stripe_checkout_url: stripeRes.data.url,
            stripe_session_id: stripeRes.data.session_id,
            stripe_qr_png_base64: stripeRes.data.qr_png_base64 || "",
            // Explicit: no bank QR for card
            qr_png_base64: "",
            iban: "",
          };
          try {
            sessionStorage.setItem("rebelky_last_order", JSON.stringify(orderPayload));
          } catch {
            /* ignore storage quota */
          }
          clearCart();
          navigate(
            res.data?.order_number
              ? `/objednavka-prijata/${res.data.order_number}?pay=stripe`
              : "/objednavka-prijata?pay=stripe",
          );
          return;
        } catch (err) {
          const detail = err.response?.data?.detail || err.message || "neznámá chyba";
          const orderPayload = {
            ...res.data,
            payment_method: "card_online",
            stripe_error: typeof detail === "string" ? detail : JSON.stringify(detail),
            qr_png_base64: "",
            iban: "",
            _id: res.data._id,
          };
          try {
            sessionStorage.setItem("rebelky_last_order", JSON.stringify(orderPayload));
          } catch {
            /* ignore */
          }
          clearCart();
          // Success page shows ERROR only — never bank QR for card
          navigate(
            res.data?.order_number
              ? `/objednavka-prijata/${res.data.order_number}?pay=stripe&stripe_error=1`
              : "/objednavka-prijata?pay=stripe&stripe_error=1",
          );
          return;
        }
      }
      try {
        sessionStorage.setItem("rebelky_last_order", JSON.stringify({
          ...res.data,
          payment_method: effectivePayment,
        }));
      } catch {
        /* ignore storage quota */
      }
      clearCart();
      navigate(res.data?.order_number ? `/objednavka-prijata/${res.data.order_number}` : "/objednavka-prijata");
    } catch (err) {
      setError(err.response?.data?.detail || "Chyba při odesílání objednávky");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    background: "#0A0A0A",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "#FFFFFF",
    padding: "0.7rem 1rem",
    borderRadius: "6px",
    fontFamily: "Manrope, sans-serif",
    fontSize: "0.9rem",
    width: "100%",
    outline: "none",
  };

  const labelStyle = {
    fontFamily: "Unbounded, sans-serif",
    fontSize: "0.55rem",
    color: "#A1A1AA",
    letterSpacing: "0.15em",
    marginBottom: "0.4rem",
    display: "block",
    textTransform: "uppercase",
  };

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title={t.checkout.seoTitle}
        description={t.checkout.seoDesc}
        url="/pokladna"
        noindex
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        <section style={{ padding: "4rem 1.5rem 2rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-5xl mx-auto">
            <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(1.5rem, 4vw, 3rem)", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "-0.03em" }}>
              {t.checkout.title}
            </h1>
          </div>
        </section>

        <form onSubmit={handleSubmit} data-testid="checkout-form">
          <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Form fields */}
            <div className="lg:col-span-2 flex flex-col gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel htmlFor="customer_name" hint={t.checkout.nameHint}>{t.checkout.name}</FieldLabel>
                  <input
                    id="customer_name"
                    name="customer_name"
                    value={form.customer_name}
                    onChange={handleChange}
                    required
                    autoComplete="name"
                    title={t.checkout.nameHint}
                    alt={t.checkout.name}
                    placeholder="Jan Novák"
                    style={{
                      ...inputStyle,
                      borderColor: nameOk ? "rgba(0,255,100,0.45)" : "rgba(255,255,255,0.1)",
                    }}
                    data-testid="input-name"
                  />
                  <FieldOk show={nameOk} text={t.checkout.nameOk} />
                </div>
                <div>
                  <FieldLabel htmlFor="customer_email" hint={t.checkout.emailHint}>{t.checkout.email}</FieldLabel>
                  <input
                    id="customer_email"
                    name="customer_email"
                    type="email"
                    value={form.customer_email}
                    onChange={handleChange}
                    required
                    autoComplete="email"
                    title={t.checkout.emailHint}
                    alt={t.checkout.email}
                    placeholder="jan@seznam.cz"
                    style={{
                      ...inputStyle,
                      borderColor: emailCheck.status === "ok"
                        ? "rgba(0,255,100,0.45)"
                        : emailCheck.status === "bad"
                          ? "rgba(255,0,90,0.55)"
                          : "rgba(255,255,255,0.1)",
                    }}
                    data-testid="input-email"
                  />
                  {emailCheck.status === "checking" && (
                    <p style={{ color: "#A1A1AA", fontSize: "0.68rem", marginTop: "0.25rem" }}>{t.checkout.emailChecking}</p>
                  )}
                  <FieldOk show={emailCheck.status === "ok"} text={t.checkout.emailOk} />
                  {emailCheck.status === "bad" && (
                    <p data-testid="email-error" style={{ color: "#FF005A", fontSize: "0.68rem", marginTop: "0.25rem" }}>
                      {emailCheck.reason}
                    </p>
                  )}
                  {emailCheck.suggestion && (
                    <p data-testid="email-typo" style={{ color: "#E5FF00", fontSize: "0.68rem", marginTop: "0.25rem" }}>
                      {t.checkout.emailTypo}{" "}
                      <button
                        type="button"
                        onClick={applyEmailSuggestion}
                        style={{ background: "transparent", border: "none", color: "#00E5FF", textDecoration: "underline", cursor: "pointer", padding: 0, fontSize: "0.68rem" }}
                      >
                        {emailCheck.suggestion}
                      </button>
                      {" "}({t.checkout.useSuggestion})
                    </p>
                  )}
                </div>
                <div>
                  <FieldLabel htmlFor="customer_phone" hint={t.checkout.phoneHint}>{t.checkout.phone}</FieldLabel>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <select
                      value={phoneCountry}
                      onChange={(e) => setPhoneCountry(e.target.value)}
                      data-testid="input-phone-country"
                      title={t.checkout.phoneHint}
                      aria-label={t.checkout.phone}
                      style={{ ...inputStyle, width: "110px", flexShrink: 0, cursor: "pointer" }}
                    >
                      <option value="+420">🇨🇿 +420</option>
                      <option value="+421">🇸🇰 +421</option>
                    </select>
                    <input
                      id="customer_phone"
                      name="customer_phone"
                      type="tel"
                      inputMode="numeric"
                      value={form.customer_phone}
                      onChange={(e) => {
                        let digits = e.target.value.replace(/\D+/g, "");
                        digits = digits.replace(/^0+/, "");
                        digits = digits.replace(/^420|^421/, "");
                        digits = digits.slice(-9);
                        setForm({ ...form, customer_phone: digits });
                      }}
                      required
                      maxLength={11}
                      placeholder="777 123 456"
                      title={t.checkout.phoneHint}
                      alt={t.checkout.phone}
                      autoComplete="tel-national"
                      style={{
                        ...inputStyle,
                        flex: 1,
                        borderColor: form.customer_phone.length === 9 ? "rgba(0,255,100,0.45)" : "rgba(255,255,255,0.1)",
                      }}
                      data-testid="input-phone"
                    />
                  </div>
                  <p style={{ fontSize: "0.7rem", color: "#52525B", marginTop: "0.3rem", fontFamily: "Manrope, sans-serif" }}>
                    {t.checkout.phoneHint}. <b style={{ color: "#A1A1AA" }}>{phoneCountry}</b>
                    {form.customer_phone.length > 0 && form.customer_phone.length < 9 && (
                      <span style={{ color: "#FF005A", display: "block", marginTop: "0.2rem" }}>
                        {form.customer_phone.length}/9
                      </span>
                    )}
                  </p>
                  <FieldOk show={form.customer_phone.length === 9} />
                </div>
                <div>
                  <FieldLabel htmlFor="customer_address" hint={t.checkout.addressHint}>{t.checkout.address}</FieldLabel>
                  <input
                    id="customer_address"
                    name="customer_address"
                    value={form.customer_address}
                    onChange={handleChange}
                    required
                    autoComplete="street-address"
                    title={t.checkout.addressHint}
                    alt={t.checkout.address}
                    placeholder="Komenského 12"
                    style={{
                      ...inputStyle,
                      borderColor: addressOk ? "rgba(0,255,100,0.45)" : "rgba(255,255,255,0.1)",
                    }}
                    data-testid="input-address"
                  />
                  <FieldOk show={addressOk} text={t.checkout.addressOk} />
                </div>
                <div>
                  <FieldLabel htmlFor="customer_city" hint={t.checkout.cityHint}>{t.checkout.city}</FieldLabel>
                  <input
                    id="customer_city"
                    name="customer_city"
                    value={form.customer_city}
                    onChange={handleChange}
                    required
                    autoComplete="address-level2"
                    title={t.checkout.cityHint}
                    alt={t.checkout.city}
                    placeholder="Praha"
                    style={{
                      ...inputStyle,
                      borderColor: cityOk ? "rgba(0,255,100,0.45)" : "rgba(255,255,255,0.1)",
                    }}
                    data-testid="input-city"
                  />
                  <FieldOk show={cityOk} />
                </div>
                <div>
                  <FieldLabel htmlFor="customer_zip" hint={t.checkout.zipHint}>{t.checkout.zip}</FieldLabel>
                  <input
                    id="customer_zip"
                    name="customer_zip"
                    value={form.customer_zip}
                    onChange={handleChange}
                    required
                    inputMode="numeric"
                    autoComplete="postal-code"
                    title={t.checkout.zipHint}
                    alt={t.checkout.zip}
                    placeholder="110 00"
                    maxLength={6}
                    style={{
                      ...inputStyle,
                      borderColor: zipOk ? "rgba(0,255,100,0.45)" : "rgba(255,255,255,0.1)",
                    }}
                    data-testid="input-zip"
                  />
                  <FieldOk show={zipOk} text={t.checkout.zipOk} />
                </div>
              </div>

              {/* Shipping method */}
              <div data-testid="shipping-section">
                <p style={{ ...labelStyle, fontSize: "0.6rem", letterSpacing: "0.2em" }}>Doprava</p>
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "0.6rem" }}>
                  {shipConfig.pickup_enabled && (
                    <label
                      data-testid="shipping-pickup-option"
                      style={{
                        display: "flex", gap: "0.8rem", padding: "1rem",
                        background: shippingMethod === "pickup" ? "rgba(255,0,90,0.07)" : "#0A0A0A",
                        border: `1px solid ${shippingMethod === "pickup" ? "rgba(255,0,90,0.5)" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: "8px", cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      <input
                        type="radio" name="shipping" checked={shippingMethod === "pickup"}
                        onChange={() => setShippingMethod("pickup")}
                        style={{ accentColor: "#FF005A", marginTop: "0.2rem", flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex justify-between items-center gap-2 flex-wrap">
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Osobní odběr
                          </span>
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "0.8rem", color: "#00E5FF" }}>ZDARMA</span>
                        </div>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem", fontFamily: "Manrope, sans-serif" }}>
                          Vyzvedni si zboží osobně po předchozí domluvě.
                        </p>
                        {shippingMethod === "pickup" && shipConfig.pickup_locations.length > 0 && (
                          <select
                            value={pickupLocation}
                            onChange={(e) => setPickupLocation(e.target.value)}
                            data-testid="pickup-location-select"
                            style={{ ...inputStyle, marginTop: "0.6rem", fontSize: "0.8rem" }}
                          >
                            {shipConfig.pickup_locations.map((loc, i) => (
                              <option key={i} value={loc.name}>
                                {loc.name} · {loc.address}, {loc.zip} {loc.city}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </label>
                  )}

                  {shipConfig.ppl_enabled && (
                    <label
                      data-testid="shipping-ppl-option"
                      style={{
                        display: "flex", gap: "0.8rem", padding: "1rem",
                        background: shippingMethod === "ppl" ? "rgba(255,0,90,0.07)" : "#0A0A0A",
                        border: `1px solid ${shippingMethod === "ppl" ? "rgba(255,0,90,0.5)" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: "8px", cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      <input
                        type="radio" name="shipping" checked={shippingMethod === "ppl"}
                        onChange={() => { setShippingMethod("ppl"); setPaymentMethod("card_online"); }}
                        style={{ accentColor: "#FF005A", marginTop: "0.2rem", flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex justify-between items-center gap-2 flex-wrap">
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            PPL – doručení na adresu
                          </span>
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "0.8rem", color: "#FF005A" }}>{shipConfig.ppl_price} Kč</span>
                        </div>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem", fontFamily: "Manrope, sans-serif" }}>
                          Doručení kurýrem PPL přímo na vaši adresu. <strong style={{ color: "#FF005A" }}>Platba: pouze online kartou.</strong>
                        </p>
                      </div>
                    </label>
                  )}

                  {shipConfig.packeta_enabled && (
                    <label
                      data-testid="shipping-packeta-option"
                      style={{
                        display: "flex", gap: "0.8rem", padding: "1rem",
                        background: shippingMethod === "packeta" ? "rgba(255,0,90,0.07)" : "#0A0A0A",
                        border: `1px solid ${shippingMethod === "packeta" ? "rgba(255,0,90,0.5)" : "rgba(255,255,255,0.1)"}`,
                        borderRadius: "8px", cursor: "pointer", transition: "all 0.15s",
                      }}
                    >
                      <input
                        type="radio" name="shipping" checked={shippingMethod === "packeta"}
                        onChange={() => setShippingMethod("packeta")}
                        style={{ accentColor: "#FF005A", marginTop: "0.2rem", flexShrink: 0 }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="flex justify-between items-center gap-2 flex-wrap">
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                            Zásilkovna
                          </span>
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "0.8rem", color: "#FF005A" }}>{shipConfig.packeta_price} Kč</span>
                        </div>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem", fontFamily: "Manrope, sans-serif" }}>
                          Doručíme na vybranou pobočku Zásilkovny.
                        </p>
                        {shippingMethod === "packeta" && (
                          <div style={{ marginTop: "0.7rem", display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                            {shipConfig.packeta_api_key ? (
                              <>
                                <button
                                  type="button"
                                  onClick={(e) => { e.preventDefault(); openPacketaWidget(); }}
                                  data-testid="packeta-widget-btn"
                                  className="btn-outline-rebel rounded-none"
                                  style={{ fontSize: "0.65rem", padding: "0.6rem 1rem", textAlign: "center" }}
                                >
                                  {packetaPoint ? "ZMĚNIT POBOČKU" : "VYBRAT POBOČKU ZÁSILKOVNY"}
                                </button>
                                {packetaPoint && (
                                  <div
                                    data-testid="packeta-selected-point"
                                    style={{
                                      padding: "0.7rem 0.9rem",
                                      background: "rgba(0,229,255,0.05)",
                                      border: "1px solid rgba(0,229,255,0.3)",
                                      borderRadius: "6px",
                                      color: "#FFFFFF",
                                      fontSize: "0.8rem",
                                      fontFamily: "Manrope, sans-serif",
                                    }}
                                  >
                                    <span style={{ color: "#00E5FF", fontSize: "0.6rem", fontFamily: "Unbounded, sans-serif", letterSpacing: "0.15em", display: "block", marginBottom: "0.2rem" }}>
                                      ✓ VYBRANÁ POBOČKA
                                    </span>
                                    {packetaPoint.name || packetaPoint.place || ""}<br />
                                    <span style={{ color: "#A1A1AA", fontSize: "0.75rem" }}>
                                      {packetaPoint.formatedValue || `${packetaPoint.street || ""}, ${packetaPoint.zip || ""} ${packetaPoint.city || ""}`}
                                    </span>
                                  </div>
                                )}
                              </>
                            ) : (
                              <input
                                placeholder="Pobočka Zásilkovny (např. Praha 5 – Anděl) nebo PSČ"
                                value={packetaBranch}
                                onChange={(e) => setPacketaBranch(e.target.value)}
                                data-testid="packeta-branch-input"
                                style={{ ...inputStyle, fontSize: "0.8rem" }}
                              />
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  )}
                </div>
              </div>

              {/* Payment methods – zobrazí jen relevantní podle shipping_method */}
              <div data-testid="payment-section">
                <p style={{
                  fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.65rem",
                  color: "#FF005A", letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "0.8rem"
                }}>
                  ★ Způsob platby
                </p>
                <div style={{ display: "grid", gap: "0.6rem" }}>
                  {/* QR / bank transfer — only when admin enabled it for this shipping */}
                  {qrAllowedForShipping && (
                  <label
                    data-testid="payment-method-qr"
                    style={{
                      cursor: "pointer", display: "flex", alignItems: "center", gap: "0.8rem",
                      padding: "0.9rem 1.1rem", borderRadius: "10px",
                      background: effectivePayment === "qr" ? "rgba(255,0,90,0.08)" : "rgba(255,255,255,0.02)",
                      border: "1px solid " + (effectivePayment === "qr" ? "rgba(255,0,90,0.45)" : "rgba(255,255,255,0.08)"),
                      transition: "all 0.2s",
                    }}
                  >
                    <input
                      type="radio" name="payment" checked={effectivePayment === "qr"}
                      onChange={() => setPaymentMethod("qr")}
                      style={{ accentColor: "#FF005A" }}
                    />
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFF", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                        QR Platba / Bankovní převod
                      </p>
                      <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem" }}>
                        Po objednávce dostaneš e-mail s QR a fakturou. Platba předem.
                      </p>
                    </div>
                    <span style={{ color: "#FF005A", fontWeight: 800, fontSize: "0.85rem" }}>0 Kč</span>
                  </label>
                  )}

                  {/* Hotově při osobním odběru */}
                  {shippingMethod === "pickup" && shipConfig.cash_on_pickup_enabled && (
                    <label
                      data-testid="payment-method-cash"
                      style={{
                        cursor: "pointer", display: "flex", alignItems: "center", gap: "0.8rem",
                        padding: "0.9rem 1.1rem", borderRadius: "10px",
                        background: effectivePayment === "cash" ? "rgba(0,229,255,0.08)" : "rgba(255,255,255,0.02)",
                        border: "1px solid " + (effectivePayment === "cash" ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.08)"),
                      }}
                    >
                      <input
                        type="radio" name="payment" checked={effectivePayment === "cash"}
                        onChange={() => setPaymentMethod("cash")}
                        style={{ accentColor: "#00E5FF" }}
                      />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFF", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Hotově při osobním odběru
                        </p>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem" }}>
                          Zaplať hotově při převzetí zboží osobně.
                        </p>
                      </div>
                      <span style={{ color: "#00E5FF", fontWeight: 800, fontSize: "0.85rem" }}>0 Kč</span>
                    </label>
                  )}

                  {/* Dobírka přes Zásilkovnu */}
                  {shippingMethod === "packeta" && shipConfig.cod_enabled && (
                    <label
                      data-testid="payment-method-cod"
                      style={{
                        cursor: "pointer", display: "flex", alignItems: "center", gap: "0.8rem",
                        padding: "0.9rem 1.1rem", borderRadius: "10px",
                        background: paymentMethod === "cod" ? "rgba(0,229,255,0.08)" : "rgba(255,255,255,0.02)",
                        border: "1px solid " + (paymentMethod === "cod" ? "rgba(0,229,255,0.45)" : "rgba(255,255,255,0.08)"),
                      }}
                    >
                      <input
                        type="radio" name="payment" checked={paymentMethod === "cod"}
                        onChange={() => setPaymentMethod("cod")}
                        style={{ accentColor: "#00E5FF" }}
                      />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFF", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Dobírka přes Zásilkovnu
                        </p>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem" }}>
                          Zaplať hotově při vyzvednutí na výdejně. Po obdržení zásilky.
                        </p>
                      </div>
                      <span style={{ color: "#00E5FF", fontWeight: 800, fontSize: "0.85rem" }}>+{shipConfig.cod_fee} Kč</span>
                    </label>
                  )}

                  {/* Karta na výdejně přes Zásilkovnu */}
                  {shippingMethod === "packeta" && shipConfig.card_on_pickup_enabled && (
                    <label
                      data-testid="payment-method-card-on-pickup"
                      style={{
                        cursor: "pointer", display: "flex", alignItems: "center", gap: "0.8rem",
                        padding: "0.9rem 1.1rem", borderRadius: "10px",
                        background: paymentMethod === "card_on_pickup" ? "rgba(229,255,0,0.08)" : "rgba(255,255,255,0.02)",
                        border: "1px solid " + (paymentMethod === "card_on_pickup" ? "rgba(229,255,0,0.45)" : "rgba(255,255,255,0.08)"),
                      }}
                    >
                      <input
                        type="radio" name="payment" checked={paymentMethod === "card_on_pickup"}
                        onChange={() => setPaymentMethod("card_on_pickup")}
                        style={{ accentColor: "#E5FF00" }}
                      />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFF", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Karta na výdejně (Zásilkovna)
                        </p>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem" }}>
                          Zaplať platební kartou přímo na výdejně Zásilkovny při převzetí.
                        </p>
                      </div>
                      <span style={{ color: "#E5FF00", fontWeight: 800, fontSize: "0.85rem" }}>
                        {shipConfig.card_on_pickup_fee > 0 ? `+${shipConfig.card_on_pickup_fee} Kč` : "ZDARMA"}
                      </span>
                    </label>
                  )}

                  {/* Karta online (Stripe) — funguje pro obě dopravy */}
                  {shipConfig.stripe_enabled && (
                    <label
                      data-testid="payment-method-card-online"
                      style={{
                        cursor: "pointer", display: "flex", alignItems: "center", gap: "0.8rem",
                        padding: "0.9rem 1.1rem", borderRadius: "10px",
                        background: paymentMethod === "card_online" ? "rgba(255,0,90,0.08)" : "rgba(255,255,255,0.02)",
                        border: "1px solid " + (paymentMethod === "card_online" ? "rgba(255,0,90,0.5)" : "rgba(255,255,255,0.08)"),
                        position: "relative",
                      }}
                    >
                      <input
                        type="radio" name="payment" checked={paymentMethod === "card_online"}
                        onChange={() => setPaymentMethod("card_online")}
                        style={{ accentColor: "#FF005A" }}
                      />
                      <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.75rem", color: "#FFF", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                          Karta online (Visa / Mastercard / Apple Pay)
                          <span style={{ marginLeft: "0.4rem", background: "#FF005A", color: "#FFF", fontSize: "0.55rem", padding: "1px 6px", borderRadius: 3, letterSpacing: "0.1em" }}>RYCHLÉ</span>
                        </p>
                        <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem" }}>
                          Zaplať ihned online přes Stripe. Bezpečně, bez čekání na bankovní převod.
                        </p>
                      </div>
                      <span style={{ color: "#FF005A", fontWeight: 800, fontSize: "0.85rem" }}>
                        {shipConfig.stripe_fee > 0 ? `+${shipConfig.stripe_fee} Kč` : "ZDARMA"}
                      </span>
                    </label>
                  )}
                </div>

                {/* Info bar pro vybranou platbu */}
                {effectivePayment === "qr" && (
                  <p style={{ color: "#A1A1AA", fontSize: "0.7rem", marginTop: "0.8rem", fontFamily: "Manrope, sans-serif" }}>
                    💳 Po dokončení objednávky obdržíš e-mail s QR kódem pro okamžitou platbu.
                  </p>
                )}
                {paymentMethod === "card_online" && (
                  <p data-testid="card-online-info" style={{ color: "#FF005A", fontSize: "0.7rem", marginTop: "0.8rem", fontFamily: "Manrope, sans-serif" }}>
                    ★ Po kliknutí na "Odeslat objednávku" budeš přesměrován na bezpečnou platební bránu Stripe.
                  </p>
                )}
                {paymentMethod === "cod" && shippingMethod === "packeta" && (
                  <p style={{ color: "#00E5FF", fontSize: "0.7rem", marginTop: "0.8rem", fontFamily: "Manrope, sans-serif" }}>
                    💰 Zásilkovna vybere hotovost při převzetí — žádné předplacení.
                  </p>
                )}
                {paymentMethod === "card_on_pickup" && shippingMethod === "packeta" && (
                  <p style={{ color: "#E5FF00", fontSize: "0.7rem", marginTop: "0.8rem", fontFamily: "Manrope, sans-serif" }}>
                    💳 Zaplať kartou přímo na výdejně Zásilkovny. Funguje na všech moderních výdejnách.
                  </p>
                )}
              </div>

              {/* Info note (shop policy) */}
              {shipConfig.order_info_note && (
                <div data-testid="order-info-note" style={{ padding: "0.9rem 1.1rem", background: "rgba(255,0,90,0.05)", borderLeft: "3px solid #FF005A", borderRadius: "6px", color: "#FFC9D9", fontSize: "0.75rem", fontFamily: "Manrope, sans-serif", lineHeight: 1.6 }}>
                  <strong style={{ display: "block", color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", letterSpacing: "0.15em", marginBottom: "0.3rem" }}>★ Informace pro tebe</strong>
                  {shipConfig.order_info_note}
                </div>
              )}

              <div>
                <label style={labelStyle}>{t.checkout.note}</label>
                <textarea
                  name="note" value={form.note} onChange={handleChange} rows={3}
                  style={{ ...inputStyle, resize: "vertical" }}
                  placeholder="Zde napiš například speciální požadavky, dárkové balení, jiný termín dodání nebo cokoliv jiného..."
                  data-testid="input-note"
                />
              </div>

              {/* Signature add-on — global settings + at least one cart product allows it */}
              {shipConfig.signature_enabled && signatureAllowedForCart && (
                <div data-testid="signature-section">
                  <label
                    data-testid="signature-toggle-label"
                    style={{
                      display: "flex", gap: "0.8rem", padding: "1rem",
                      background: signatureOn ? "rgba(255,0,90,0.07)" : "#0A0A0A",
                      border: `1px solid ${signatureOn ? "rgba(255,0,90,0.5)" : "rgba(255,255,255,0.1)"}`,
                      borderRadius: "8px", cursor: "pointer", transition: "all 0.15s",
                    }}
                  >
                    <input
                      type="checkbox" checked={signatureOn}
                      onChange={(e) => setSignatureOn(e.target.checked)}
                      data-testid="signature-checkbox"
                      style={{ accentColor: "#FF005A", marginTop: "0.2rem", flexShrink: 0, width: 16, height: 16 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="flex justify-between items-center gap-2 flex-wrap">
                        <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, fontSize: "0.7rem", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                          ★ {shipConfig.signature_label}
                        </span>
                        <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "0.8rem", color: "#FF005A" }}>+{shipConfig.signature_price} Kč</span>
                      </div>
                      <p style={{ color: "#A1A1AA", fontSize: "0.75rem", marginTop: "0.2rem", fontFamily: "Manrope, sans-serif", lineHeight: 1.5 }}>
                        {t.checkout.signatureHint}
                      </p>
                      {signatureOn && (
                        <input
                          placeholder={t.checkout.signaturePlaceholder}
                          value={signatureTo}
                          onChange={(e) => setSignatureTo(e.target.value)}
                          maxLength={60}
                          data-testid="signature-to-input"
                          style={{ ...inputStyle, marginTop: "0.6rem", fontSize: "0.8rem" }}
                        />
                      )}
                    </div>
                  </label>
                </div>
              )}

              {/* GDPR / Terms consent */}
              <label
                data-testid="gdpr-consent-label"
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "0.7rem",
                  padding: "1rem",
                  background: "#0A0A0A",
                  border: `1px solid ${consent ? "rgba(255,0,90,0.4)" : "rgba(255,255,255,0.1)"}`,
                  borderRadius: "8px",
                  cursor: "pointer",
                  transition: "border-color 0.2s",
                }}
              >
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="gdpr-consent-checkbox"
                  style={{ accentColor: "#FF005A", marginTop: "0.2rem", width: 16, height: 16, flexShrink: 0 }}
                />
                <span style={{ color: "#A1A1AA", fontSize: "0.8rem", lineHeight: 1.5, fontFamily: "Manrope, sans-serif" }}>
                  {t.checkout.consentBefore}{" "}
                  <Link to="/obchodni-podminky" target="_blank" style={{ color: "#FF005A", textDecoration: "underline" }}>
                    {t.checkout.terms}
                  </Link>{" "}
                  {t.checkout.consentAnd}{" "}
                  <Link to="/gdpr" target="_blank" style={{ color: "#FF005A", textDecoration: "underline" }}>
                    {t.checkout.privacy}
                  </Link>
                  {t.checkout.consentAfter}{" "}
                  <Link to="/doruceni" target="_blank" style={{ color: "#FF005A", textDecoration: "underline" }}>
                    {t.checkout.delivery}
                  </Link>
                  .
                </span>
              </label>

              {error && <p style={{ color: "#FF005A", fontSize: "0.85rem" }} data-testid="checkout-error">{error}</p>}
            </div>

            {/* Order summary */}
            <div>
              <div className="glass-card rounded-xl p-6 lg:sticky lg:top-20">
                <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.6rem", color: "#FF005A", letterSpacing: "0.2em", marginBottom: "1rem" }}>
                  {t.checkout.orderSummary.toUpperCase()}
                </p>
                {items.map((item, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-3 mb-2" data-testid={`summary-item-${i}`}>
                    <span style={{ color: "#A1A1AA", fontSize: "0.8rem", minWidth: 0, flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {item.name}
                      <span style={{ marginLeft: "0.35rem", color: "#71717A", flexShrink: 0 }}>×{item.quantity}</span>
                    </span>
                    <span style={{ color: "#FFFFFF", fontSize: "0.8rem", flexShrink: 0, whiteSpace: "nowrap" }}>{item.price * item.quantity} Kč</span>
                  </div>
                ))}
                <div style={{ borderTop: "1px solid rgba(255,255,255,0.08)", marginTop: "1rem", paddingTop: "1rem" }}>
                  <div className="flex justify-between mb-2">
                    <span style={{ color: "#A1A1AA", fontSize: "0.85rem" }} data-testid="summary-shipping-label">
                      {shippingMethod === "pickup" ? t.checkout.pickup : (shippingMethod === "ppl" ? "PPL" : t.checkout.packeta)}
                    </span>
                    <span style={{ color: shippingPrice === 0 ? "#00E5FF" : "#FFFFFF", fontSize: "0.85rem" }} data-testid="summary-shipping-price">
                      {shippingPrice === 0 ? t.checkout.free : `${shippingPrice} Kč`}
                    </span>
                  </div>
                  {signaturePrice > 0 && (
                    <div className="flex justify-between mb-2" data-testid="summary-signature-row">
                      <span style={{ color: "#A1A1AA", fontSize: "0.85rem" }}>★ {t.checkout.signatureFor} {signatureTo.trim()}</span>
                      <span style={{ color: "#FF005A", fontSize: "0.85rem" }}>+{signaturePrice} Kč</span>
                    </div>
                  )}
                  {paymentFee > 0 && (
                    <div className="flex justify-between mb-2" data-testid="summary-payment-fee-row">
                      <span style={{ color: "#A1A1AA", fontSize: "0.85rem" }}>
                        {paymentMethod === "cod" ? t.checkout.cashOnDelivery : t.checkout.cardOnPickup}
                      </span>
                      <span style={{ color: "#00E5FF", fontSize: "0.85rem" }}>+{paymentFee} Kč</span>
                    </div>
                  )}
                  <div className="flex justify-between mb-4">
                    <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 700, color: "#FFFFFF" }}>{t.checkout.orderTotal}</span>
                    <span style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 800, color: "#FF005A", fontSize: "1.1rem" }} data-testid="summary-total">{grandTotal} Kč</span>
                  </div>
                  <button
                    type="submit"
                    disabled={loading || items.length === 0 || !consent}
                    data-testid="submit-order-btn"
                    className="btn-rebel rounded-none w-full"
                    style={{ opacity: loading || items.length === 0 || !consent ? 0.5 : 1, cursor: loading ? "wait" : "pointer" }}
                  >
                    {loading ? t.checkout.submitting : t.checkout.submit}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}
